import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const HERO = "https://images.unsplash.com/photo-1618244965061-1d27b208d6e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const M1 = "https://images.unsplash.com/photo-1562349486-3355f0c8cefa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const M2 = "https://images.unsplash.com/photo-1664076458686-3449062080ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const Line = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const chapters = [
  { n: "01", t: "Made in small runs", d: "Every piece is produced in limited quantities. No mass production, no waste — only what deserves to exist." },
  { n: "02", t: "Fabric that lasts", d: "Silk, linen, cashmere-blends. We choose materials that age beautifully and feel like a second skin." },
  { n: "03", t: "Cut to flatter", d: "Silhouettes designed on real women. Considered proportions that move with you, season after season." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-end overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0 -z-10">
          <img src={HERO} alt="Editorial" className="img-cover" />
          <div className="absolute inset-0 bg-ink/40" />
        </motion.div>
        <div className="max-w-[1600px] mx-auto w-full px-5 sm:px-8 pb-16 sm:pb-24 text-pearl">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 1 }} className="label-caps mb-6">
            New Arrivals — Spring 2026
          </motion.p>
          <h1 className="font-serif-display font-light tracking-tighter leading-[0.9] text-5xl sm:text-7xl lg:text-8xl">
            <Line delay={0.1}>Wardrobe</Line>
            <Line delay={0.25}><span className="italic">essentials</span>, made</Line>
            <Line delay={0.4}>to be lived in.</Line>
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }} className="mt-10">
            <Link to="/shop" className="group inline-flex items-center gap-3 border border-pearl px-8 py-4 label-caps hover:bg-pearl hover:text-ink transition-colors duration-300" data-testid="hero-shop-btn">
              Shop the collection
              <ArrowRight strokeWidth={1.5} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-line py-5 bg-surface">
        <Marquee speed={38} gradient={false}>
          {["Silk", "Linen", "Cashmere", "Handmade", "Small Batch", "Timeless"].map((w, i) => (
            <span key={i} className="font-serif-display text-3xl sm:text-4xl italic mx-12 text-ink/80">{w} <span className="not-italic mx-2">✦</span></span>
          ))}
        </Marquee>
      </div>

      {/* FEATURED */}
      <section className="max-w-[1600px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="label-caps text-muted-foreground mb-3">Curated for you</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-light tracking-tight">Featured pieces</h2>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-2 label-caps hover:text-wine transition-colors" data-testid="view-all-btn">
            View all <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="bg-ink text-pearl">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-20 sm:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
              <p className="label-caps text-pearl/50 mb-6">Our philosophy</p>
              <h2 className="font-serif-display text-4xl sm:text-6xl font-light leading-[1.05]">
                Fewer, <span className="italic">better</span> things — chosen with intention.
              </h2>
            </motion.div>
            <div className="grid grid-cols-2 gap-4">
              <div className="overflow-hidden aspect-[3/4]"><img src={M1} alt="Editorial" className="img-cover" /></div>
              <div className="overflow-hidden aspect-[3/4] mt-12"><img src={M2} alt="Editorial" className="img-cover" /></div>
            </div>
          </div>
          <div className="space-y-px">
            {chapters.map((c, i) => (
              <motion.div key={c.n} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.1 }}
                className="grid md:grid-cols-12 gap-6 items-baseline py-10 border-t border-pearl/15">
                <span className="font-serif-display text-6xl sm:text-7xl md:col-span-3 text-pearl/30">{c.n}</span>
                <h3 className="font-serif-display text-2xl sm:text-3xl md:col-span-4">{c.t}</h3>
                <p className="text-pearl/60 leading-relaxed md:col-span-5 text-sm">{c.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
